import type { BlockGuide } from "./types";

export const guide: BlockGuide = {
  slug: "discord",
  app: "Discord",
  title: "How to block Discord on iPhone without leaving your friends",
  h1: "How to block Discord",
  description:
    "Screen Time steps, why unread badges across a dozen servers work differently from a feed, and how to stay in the community without living in it.",
  targets: [
    "how to block discord on iphone",
    "discord too distracting",
    "how to limit discord time",
    "quit discord servers",
  ],
  published: "2026-09-03",
  standfirst:
    "Discord has no algorithm and no feed. It gets you a different way: by being where your friends are, all the time, with a red dot for every room you are not in.",
  takeaways: [
    "The pull is presence, not content. Your friends are visibly online, and being absent from a conversation you can see happening is the cost.",
    "Unread badges across a dozen servers create an endless clearing task with no bottom, which is a different mechanic from a feed and needs a different fix.",
    "Leave servers rather than limiting time. Two active servers is a social life; fourteen is a notification job.",
  ],
  mechanic: [
    "Discord is the one app on this list with no recommendation engine at all. There is no algorithm choosing what you see, no infinite scroll, and nothing designed to be addictive in the way a video feed is. It still eats evenings, and the reason is more interesting than any of the others.",
    "The mechanic is presence. Discord shows you who is online right now, in which channel, doing what, and it does this continuously. Being not-there is therefore visible, both to you and to them, and a conversation you can see happening without you is a considerably stronger pull than any recommendation. That is not manipulation; it is what a place feels like, which is why people say they hang out on Discord rather than that they use it.",
    "On top of that sits the unread system. Every server, every channel and every thread carries its own badge, and the number of them grows with each server you join. Clearing them is an endless task that produces the feeling of an inbox without any of the value — and unlike a feed, it does not end when you get bored, because it is not entertainment. It is an obligation with a counter.",
  ],
  limitFails:
    "A time limit treats Discord like entertainment, and for most users it is a social space with real relationships in it, so the limit fires in the middle of a conversation with a friend and gets dismissed for a reason that is genuinely good. The other failure is that the badges do not care about your limit: a person who has closed Discord is still aware of fourteen servers accumulating unread messages, and that awareness is most of what the app costs them.",
  instead: [
    "Leave servers. This is the fix, and everything else is a workaround. Keep the two or three where you actually talk to people and leave the rest, including the ones you joined for a single question in 2023.",
    "In each remaining server, mute every channel except the two you read, and set notifications to mentions only.",
    "Turn off the badge on the app icon entirely — Settings → Notifications → Discord → Badges off. The number is what pulls, not the messages.",
    "If a specific server is the whole problem, leaving that one server does more than any Screen Time setting will.",
  ],
  verses: [
    {
      verse: {
        text: 'Jesus answered her, "Martha, Martha, you are anxious and troubled about many things, but one thing is needed."',
        ref: "Luke 10:41–42",
      },
      note: "Martha is not doing anything wrong; she is doing several good things at once while something better goes unattended. Many things is a diagnosis rather than an accusation, and it fits a screen of fourteen servers better than almost any other sentence in the New Testament fits anything modern.",
    },
    {
      verse: {
        text: "Two are better than one, because they have a good reward for their labor. For if they fall, the one will lift up his fellow; but woe to him who is alone when he falls, and doesn't have another to lift him up.",
        ref: "Ecclesiastes 4:9–10",
      },
      note: "The most unsentimental argument for friendship in Scripture, and it is worth quoting on a page about leaving a social app because it cuts both ways. The test it applies is who is close enough to pick you up — which some Discord servers genuinely satisfy and most of them do not, and being honest about which is which is the whole exercise.",
    },
  ],
  body: [
    {
      type: "h2",
      text: "Count your servers",
      id: "count-your-servers",
    },
    {
      type: "p",
      text: "Open the sidebar and count. Most people have somewhere between eight and thirty, and can name two where they have spoken in the last month. The rest are producing badges, notifications and a low-level sense of being behind, in exchange for nothing.",
    },
    {
      type: "p",
      text: "Leaving them is instant, reversible and by far the most effective thing available here.",
    },
    {
      type: "h2",
      text: "Presence is the cost",
      id: "presence-is-the-cost",
    },
    {
      type: "p",
      text: "The reason Discord feels different from other apps is that it is genuinely social — the people are real, the conversations are real, and the fear of missing them is not manufactured. That makes the usual advice about digital detoxes land badly, and it should.",
    },
    {
      type: "note",
      text: "The honest question is not how many hours but whether these are friendships that survive you being absent for a week. Two or three usually are. The rest are a room you happen to be in.",
    },
    {
      type: "h2",
      text: "Kill the badge",
      id: "kill-the-badge",
    },
    {
      type: "p",
      text: "The red number is the single most effective thing on the icon, and iOS lets you turn it off without touching notifications. Doing that converts Discord from something that summons you into something you visit.",
    },
    {
      type: "p",
      text: "It is the same principle as {taking the icon off the home screen}: the app is not the problem so much as the constant small invitation to open it.",
      links: {
        "taking the icon off the home screen": "/block/instagram",
      },
    },
  ],
  faq: [
    {
      q: "How do I block Discord on my iPhone?",
      a: "Screen Time → App Limits → Social Networking with a passcode, but for Discord the effective move is leaving servers and turning off the icon badge — the pull is presence and unread counts rather than content.",
    },
    {
      q: "Why is Discord so distracting if it has no algorithm?",
      a: "Because the mechanic is presence rather than recommendation. You can see who is online and which conversation is happening without you, and that is a stronger pull than any feed.",
    },
    {
      q: "How many Discord servers should I be in?",
      a: "As many as you actually talk in, which for nearly everyone is two or three. The rest generate badges and a sense of being behind in exchange for nothing.",
    },
  ],
  relatedApps: ["snapchat", "reddit", "mobile-games"],
  related: ["dopamine-detox-christian", "screen-time-limits-dont-work"],
  relatedPrayers: ["prayer-for-focus", "prayer-for-loneliness"],
};
